import React, { useEffect, useState, useCallback } from "react";
import { logsApi } from "../services/logsApi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Database,
  AlertTriangle,
  Server,
  Clock,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import CustomSelect from "./ui/CustomSelect";
import type { SelectOption } from "./ui/CustomSelect";
import Skeleton, { SkeletonCard, SkeletonChart } from "./ui/Skeleton";

const t = {
  primary: "#7c3aed",
  primaryMid: "#a78bfa",
  primaryLight: "#ede9fe",
  surface: "#ffffff",
  border: "#e9e5f5",
  danger: "#ef4444",
  dangerBg: "#fee2e2",
  warn: "#f59e0b",
  warnBg: "#fef3c7",
  info: "#3b82f6",
  infoBg: "#dbeafe",
  text: {
    primary: "#1e1b4b",
    secondary: "#6b7280",
    muted: "#9ca3b8",
    inverse: "#ffffff",
  },
};

const COLORS = [
  "#7c3aed",
  "#a78bfa",
  "#3b82f6",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#6d28d9",
  "#8b5cf6",
];
const PLATFORM_COLORS: Record<string, string> = {
  web: "#3b82f6",
  android: "#22c55e",
  ios: "#f59e0b",
  admin: "#7c3aed",
  unknown: "#94a3b8",
};

interface StatsData {
  total: number;
  byLevel: Array<{ key: string; doc_count: number }>;
  byService: Array<{ key: string; doc_count: number }>;
  byHour: Array<{ key: number; key_as_string: string; doc_count: number }>;
  errorCount: number;
  byUser?: Array<{ key: string; doc_count: number; name?: string | null }>;
}

// ── Metric Card ──
function MetricCard({
  label,
  value,
  icon,
  sub,
  color,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  sub?: { text: string; severity?: "danger" | "warn" | "neutral" };
  color?: string;
}) {
  const subStyles =
    sub?.severity === "danger"
      ? { bg: t.dangerBg, fg: t.danger }
      : sub?.severity === "warn"
        ? { bg: t.warnBg, fg: t.warn }
        : { bg: t.primaryLight, fg: t.primary };

  return (
    <div
      className="rounded-2xl p-5 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5"
      style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}
    >
      <div className="flex items-start justify-between mb-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: color ? `${color}15` : t.primaryLight }}
        >
          {icon}
        </div>
        {sub && (
          <span
            className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: subStyles.bg, color: subStyles.fg }}
          >
            {sub.text}
          </span>
        )}
      </div>
      <p className="text-[22px] font-bold" style={{ color: t.text.primary }}>
        {value}
      </p>
      <p className="text-[12px] mt-0.5" style={{ color: t.text.secondary }}>
        {label}
      </p>
    </div>
  );
}

// ── Skeleton for Stats page ──
function StatsSkeleton() {
  return (
    <div>
      {/* Title */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Skeleton width={200} height={26} className="mb-2" />
          <Skeleton width={140} height={14} />
        </div>
        <div className="flex gap-3">
          <Skeleton width={130} height={36} rounded="xl" />
          <Skeleton width={100} height={36} rounded="xl" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>

      {/* Charts */}
      <div className="space-y-5">
        <SkeletonChart />
        <SkeletonChart />
        <SkeletonChart />
      </div>
    </div>
  );
}

// ── Main Component ──
export default function Statistics() {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("24h");
  const [platformStats, setPlatformStats] = useState<any>(null);

  const timeRangeOptions: SelectOption[] = [
    { value: "1h", label: "1 heure" },
    { value: "6h", label: "6 heures" },
    { value: "24h", label: "24 heures" },
    { value: "7d", label: "7 jours" },
    { value: "30d", label: "30 jours" },
  ];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date();
      switch (timeRange) {
        case "1h":
          startDate.setHours(startDate.getHours() - 1);
          break;
        case "6h":
          startDate.setHours(startDate.getHours() - 6);
          break;
        case "24h":
          startDate.setDate(startDate.getDate() - 1);
          break;
        case "7d":
          startDate.setDate(startDate.getDate() - 7);
          break;
        case "30d":
          startDate.setDate(startDate.getDate() - 30);
          break;
      }

      const [statsRes, platformRes] = await Promise.all([
        logsApi.getStatsOverview(
          startDate.toISOString(),
          endDate.toISOString(),
        ),
        logsApi.getPlatformStats(
          startDate.toISOString(),
          endDate.toISOString(),
        ),
      ]);

      if (statsRes.success) setStats(statsRes.data);
      if (platformRes.success) setPlatformStats(platformRes.data);
    } catch (err) {
      console.error("Statistics load error:", err);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Derived data ──
  const errorRate = stats
    ? ((stats.errorCount / (stats.total || 1)) * 100).toFixed(1)
    : "0";
  const warnCount =
    stats?.byLevel?.find((l) => l.key === "warn")?.doc_count || 0;
  const serviceCount =
    stats?.byService?.filter((s) => s.key !== "logs-api").length || 0;

  // Logs par service / jour
  const servicesByDayRaw = platformStats?.servicesByDay || [];
  const serviceTotals: Record<string, number> = {};
  servicesByDayRaw.forEach((bucket: any) => {
    bucket.by_service?.buckets?.forEach((s: any) => {
      if (s.key !== "logs-api")
        serviceTotals[s.key] = (serviceTotals[s.key] || 0) + s.doc_count;
    });
  });
  const topServices = Object.entries(serviceTotals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([key]) => key);
  const servicesByDayData = servicesByDayRaw.map((bucket: any) => {
    const entry: Record<string, any> = { date: bucket.key_as_string };
    topServices.forEach((svc) => {
      entry[svc] = 0;
    });
    bucket.by_service?.buckets?.forEach((s: any) => {
      if (topServices.includes(s.key)) entry[s.key] = s.doc_count;
    });
    return entry;
  });

  // Logs par plateforme / jour
  const platformsByDayMap: Record<string, Record<string, number>> = {};
  (platformStats?.platforms || []).forEach((p: any) => {
    p.timeline?.forEach((t: any) => {
      if (!platformsByDayMap[t.key_as_string])
        platformsByDayMap[t.key_as_string] = {};
      platformsByDayMap[t.key_as_string][p.platform] = t.doc_count;
    });
  });
  const platformsByDayData = Object.entries(platformsByDayMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, plats]) => ({ date, ...plats }));
  const activePlatforms: string[] = (platformStats?.platforms || [])
    .map((p: any) => p.platform as string)
    .filter(
      (p: string, i: number, arr: string[]) =>
        p !== "unknown" && arr.indexOf(p) === i,
    );

  // Logs par utilisateur
  const userData = (stats?.byUser || []).filter(
    (u: { key: string; doc_count: number; name?: string | null }) =>
      u.key !== "logs-api",
  );

  if (loading) return <StatsSkeleton />;

  return (
    <div className="animate-fade-in">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-[22px] font-bold"
            style={{ color: t.text.primary }}
          >
            Statistiques
          </h2>
          <p className="text-[13px] mt-0.5" style={{ color: t.text.secondary }}>
            Vue d'ensemble de l'activité API
          </p>
        </div>
        <div className="flex items-center gap-3">
          <CustomSelect
            value={timeRange}
            onChange={setTimeRange}
            options={timeRangeOptions}
            icon={<Clock className="w-3.5 h-3.5" />}
            size="sm"
            className="w-[140px]"
          />
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium text-white transition-all duration-200"
            style={{ backgroundColor: t.primary, border: "none" }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger-children">
        <MetricCard
          label="Total Appels API"
          value={stats?.total?.toLocaleString() || "0"}
          icon={<Database className="w-5 h-5" style={{ color: t.primary }} />}
        />
        <MetricCard
          label="Erreurs"
          value={stats?.errorCount?.toLocaleString() || "0"}
          icon={
            <AlertTriangle className="w-5 h-5" style={{ color: t.danger }} />
          }
          sub={{ text: `${errorRate}% du total`, severity: "danger" }}
          color={t.danger}
        />
        <MetricCard
          label="Avertissements"
          value={warnCount.toLocaleString()}
          icon={<AlertCircle className="w-5 h-5" style={{ color: t.warn }} />}
          color={t.warn}
        />
        <MetricCard
          label="Services Actifs"
          value={String(serviceCount)}
          icon={<Server className="w-5 h-5" style={{ color: t.primary }} />}
        />
      </div>

      {/* ── Hourly Heatmap / Activity Bar ── */}
      <div
        className="mt-5 rounded-2xl p-5"
        style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}
      >
        <div className="mb-4">
          <h3
            className="text-[15px] font-bold"
            style={{ color: t.text.primary }}
          >
            Volume Horaire
          </h3>
          <p className="text-[11px] mt-0.5" style={{ color: t.text.secondary }}>
            Nombre d'appels par créneau
          </p>
        </div>

        <div className="h-[160px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={
                stats?.byHour?.slice(-24).map((h) => ({
                  hour: h.key_as_string?.split(" ")[1] || h.key_as_string,
                  count: h.doc_count,
                })) || []
              }
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f3f0ff"
                vertical={false}
              />
              <XAxis
                dataKey="hour"
                fontSize={10}
                stroke={t.text.muted}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                fontSize={10}
                stroke={t.text.muted}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: t.surface,
                  border: `1px solid ${t.border}`,
                  borderRadius: "12px",
                  fontSize: "12px",
                }}
              />
              <Bar
                dataKey="count"
                name="Appels"
                fill={t.primary}
                radius={[4, 4, 0, 0]}
                maxBarSize={16}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Logs par Service / Jour ── */}
      {servicesByDayData.length > 0 && (
        <div
          className="mt-5 rounded-2xl p-5"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="flex items-center justify-between mb-1">
            <h3
              className="text-[15px] font-bold"
              style={{ color: t.text.primary }}
            >
              Logs par Service / Jour
            </h3>
            <span
              className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
              style={{ backgroundColor: t.primaryLight, color: t.primary }}
            >
              Top {topServices.length} services
            </span>
          </div>
          <p className="text-[11px] mb-4" style={{ color: t.text.secondary }}>
            Volume quotidien par service
          </p>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={servicesByDayData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f3f0ff"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  fontSize={10}
                  stroke={t.text.muted}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  fontSize={10}
                  stroke={t.text.muted}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: t.surface,
                    border: `1px solid ${t.border}`,
                    borderRadius: "12px",
                    fontSize: "12px",
                  }}
                />
                <Legend
                  iconSize={8}
                  iconType="circle"
                  wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }}
                />
                {topServices.map((svc, i) => (
                  <Bar
                    key={svc}
                    dataKey={svc}
                    stackId="s"
                    fill={COLORS[i % COLORS.length]}
                    radius={
                      i === topServices.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]
                    }
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Logs par Plateforme / Jour ── */}
      {platformsByDayData.length > 0 && (
        <div
          className="mt-5 rounded-2xl p-5"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="mb-4">
            <h3
              className="text-[15px] font-bold"
              style={{ color: t.text.primary }}
            >
              Logs par Plateforme / Jour
            </h3>
            <p
              className="text-[11px] mt-0.5"
              style={{ color: t.text.secondary }}
            >
              Répartition quotidienne par plateforme
            </p>
          </div>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={platformsByDayData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f3f0ff"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  fontSize={10}
                  stroke={t.text.muted}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  fontSize={10}
                  stroke={t.text.muted}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: t.surface,
                    border: `1px solid ${t.border}`,
                    borderRadius: "12px",
                    fontSize: "12px",
                  }}
                />
                <Legend
                  iconSize={8}
                  iconType="circle"
                  wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }}
                />
                {activePlatforms.map((platform, i) => (
                  <Bar
                    key={platform}
                    dataKey={platform}
                    stackId="p"
                    fill={
                      PLATFORM_COLORS[platform] || COLORS[i % COLORS.length]
                    }
                    radius={
                      i === activePlatforms.length - 1
                        ? [4, 4, 0, 0]
                        : [0, 0, 0, 0]
                    }
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Logs par Utilisateur ── */}
      {userData.length > 0 && (
        <div
          className="mt-5 rounded-2xl p-5"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="mb-4">
            <h3
              className="text-[15px] font-bold"
              style={{ color: t.text.primary }}
            >
              Logs par Utilisateur
            </h3>
            <p
              className="text-[11px] mt-0.5"
              style={{ color: t.text.secondary }}
            >
              {userData.length} utilisateur{userData.length > 1 ? "s" : ""} ·
              classés par volume
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                  <th
                    className="text-left py-2.5 px-2 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    #
                  </th>
                  <th
                    className="text-left py-2.5 px-2 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Utilisateur
                  </th>
                  <th
                    className="text-right py-2.5 px-2 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Logs
                  </th>
                  <th
                    className="text-right py-2.5 px-2 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    %
                  </th>
                </tr>
              </thead>
              <tbody>
                {userData.map((user, i) => {
                  const pct = stats?.total
                    ? ((user.doc_count / stats.total) * 100).toFixed(1)
                    : "0";
                  const maxCount = userData[0]?.doc_count || 1;
                  const barWidth = ((user.doc_count / maxCount) * 100).toFixed(
                    0,
                  );
                  return (
                    <tr
                      key={i}
                      className="group transition-colors hover:bg-[#faf8ff]"
                      style={{ borderBottom: `1px solid ${t.border}` }}
                    >
                      <td
                        className="py-2.5 px-2 text-[12px] font-semibold"
                        style={{ color: t.text.muted }}
                      >
                        {i + 1}
                      </td>
                      <td className="py-2.5 px-2">
                        <div className="flex items-center gap-3">
                          <div className="min-w-[80px] max-w-[240px]">
                            <p
                              className="text-[12px] truncate font-medium"
                              style={{ color: t.text.primary }}
                            >
                              {user.name || user.key}
                            </p>
                            {user.name && user.key !== "anonymous" && (
                              <p
                                className="text-[10px] truncate"
                                style={{ color: t.text.muted }}
                              >
                                {user.key}
                              </p>
                            )}
                            <div
                              className="mt-1 h-1 rounded-full"
                              style={{ backgroundColor: "#f3f0ff" }}
                            >
                              <div
                                className="h-1 rounded-full"
                                style={{
                                  width: `${barWidth}%`,
                                  backgroundColor: t.primary,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        className="py-2.5 px-2 text-[12px] text-right font-semibold"
                        style={{ color: t.primary }}
                      >
                        {user.doc_count.toLocaleString()}
                      </td>
                      <td className="py-2.5 px-2 text-right">
                        <span
                          className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: t.primaryLight,
                            color: t.primary,
                          }}
                        >
                          {pct}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
